import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { toast } from "sonner";
import { useCustomerAuth } from "./CustomerAuthContext";

const CartContext = createContext(null);
const CART_STORAGE_KEY = "frida-cicekcilik-cart";

function readStoredCart() {
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function CartProvider({ children }) {
  const { token: customerToken } = useCustomerAuth();
  // Sayfa yenilendiğinde sepetin boşalmaması için localStorage'dan başlatılır
  // (sipariş verilerinin gizliliği açısından sorun yok — yalnızca ürün id/adet).
  const [cart, setCart] = useState(readStoredCart);
  const [submittingOrder, setSubmittingOrder] = useState(false);
  const [lastOrder, setLastOrder] = useState(null); // { id, phone } — teşekkür sayfasında ve takip için

  useEffect(() => {
    try {
      window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch {
      // localStorage kullanılamıyorsa (gizli sekme kotası vb.) sessizce geç —
      // sepet o oturum için sadece bellekte kalır.
    }
  }, [cart]);

  const addToCart = useCallback((product) => {
    setCart((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) {
        return prev.map((p) => (p.id === product.id ? { ...p, qty: p.qty + 1 } : p));
      }
      return [...prev, { ...product, qty: 1 }];
    });
    toast.success(`${product.name} sepete eklendi`, {
      description: "Sepetinizi her zaman sağ üstten kontrol edebilirsiniz.",
    });
  }, []);

  const increment = useCallback(
    (id) => setCart((prev) => prev.map((p) => (p.id === id ? { ...p, qty: p.qty + 1 } : p))),
    []
  );
  const decrement = useCallback(
    (id) =>
      setCart((prev) => prev.map((p) => (p.id === id ? { ...p, qty: Math.max(1, p.qty - 1) } : p))),
    []
  );
  const remove = useCallback((id) => setCart((prev) => prev.filter((p) => p.id !== id)), []);

  // Sepeti sunucu tarafındaki /api/order.php ucuna gönderir; o da ZEO'nun
  // herkese açık sipariş uç noktasına iletir (fiyat/stok orada panelin
  // gerçek ürün kataloğundan doğrulanır — burada sadece ürün kimliği ve
  // adet gönderilir, isim/fiyat değil).
  const submitOrder = useCallback(async (customer) => {
    setSubmittingOrder(true);
    try {
      const res = await fetch("/api/order.php", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(customerToken ? { "X-Customer-Token": customerToken } : {}),
        },
        body: JSON.stringify({
          customer_name: customer.name,
          customer_phone: customer.phone,
          customer_email: customer.email || "",
          il: customer.il,
          ilce: customer.ilce,
          address_detail: customer.addressDetail,
          note: customer.note || "",
          sender_name: customer.senderName || "",
          delivery_date: customer.deliveryDate || "",
          delivery_start_time: customer.deliveryStart || "",
          delivery_end_time: customer.deliveryEnd || "",
          items: cart.map((i) => ({ id: i.id, qty: i.qty })),
        }),
      });
      const data = await res.json().catch(() => null);

      if (!res.ok || data?.status !== "success") {
        toast.error(data?.message || "Sipariş gönderilemedi", {
          description: "Lütfen WhatsApp veya telefon ile iletişime geçin.",
        });
        return false;
      }

      toast.success("Siparişiniz alındı", {
        description: "En kısa sürede sizinle iletişime geçeceğiz.",
      });
      setLastOrder({ id: data.order_id ?? null, number: data.order_number ?? null, phone: customer.phone });
      setCart([]);
      return data.order_id
        ? { orderId: data.order_id, orderNumber: data.order_number ?? null, orderToken: data.order_token ?? null }
        : false;
    } catch {
      toast.error("Sipariş gönderilemedi", {
        description: "Bağlantı sorunu — lütfen WhatsApp veya telefon ile iletişime geçin.",
      });
      return false;
    } finally {
      setSubmittingOrder(false);
    }
  }, [cart, customerToken]);

  // Sipariş oluştuktan hemen sonra çağrılır. İşletmenin hiç online ödeme
  // sağlayıcısı aktif etmediği durumda ("not_configured") null döner —
  // çağıran taraf bunu eski akışa (doğrudan teşekkür sayfası) geri dönerek
  // ele almalı. İşletmenin BİRDEN FAZLA aktif sağlayıcısı varsa ve `provider`
  // belirtilmemişse, sunucu hiçbir sağlayıcıyı başlatmadan seçenekleri
  // döner ({needsSelection:true, providers}) — çağıran taraf bir seçim UI'ı
  // gösterip aynı fonksiyonu bu sefer `provider` ile tekrar çağırmalı.
  const initiatePayment = useCallback(async (orderId, orderToken, provider) => {
    try {
      const res = await fetch("/api/initiate_payment.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          order_id: orderId,
          order_token: orderToken,
          ...(provider ? { provider } : {}),
        }),
      });
      const data = await res.json().catch(() => null);
      if (data?.status === "success" && data.iframe_url) {
        return { iframeUrl: data.iframe_url, provider: data.provider ?? provider ?? null };
      }
      if (data?.status === "needs_provider_selection" && Array.isArray(data.providers)) {
        return { needsSelection: true, providers: data.providers };
      }
      return null;
    } catch {
      return null;
    }
  }, []);

  const value = {
    cart, addToCart, increment, decrement, remove, initiatePayment,
    submitOrder, submittingOrder, lastOrder,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within a CartProvider");
  return ctx;
}
